import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import GenreRow from '../components/GenreRow';
import { genreLabel } from '../constants/genreLabels';
import AnimeCard from '../components/AnimeCard';
import RatingBadge from '../components/RatingBadge';
import { statusLabel } from '../constants/statusLabels';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// ── Hero carousel ─────────────────────────────────────────────────────────────

// ── Genre rows ────────────────────────────────────────────────────────────────
const HOME_GENRES = [
  'Action', 'Adventure', 'Comedy', 'Drama',
  'Fantasy', 'Romance', 'Sci-Fi', 'Horror',
  'Mystery', 'Slice of Life',
];

// ── History helpers ───────────────────────────────────────────────────────────
const HISTORY_KEY = 'ashveil_history';
const getHistoryLocal = () => {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
  catch { return []; }
};

// ── AiringCard ────────────────────────────────────────────────────────────────
function AiringCard({ anime }) {
  const navigate = useNavigate();
  return (
    <div className="airing-card" onClick={() => navigate(`/anime/${anime.id}`)}>
      {anime.image && (
        <img src={anime.image} alt={anime.title} referrerPolicy="no-referrer" loading="lazy" />
      )}
      {anime.latest_ep != null && (
        <span className="airing-badge-ep">EP. {anime.latest_ep}</span>
      )}
      {anime.mal_score && (
        <span className="airing-badge-score">★ {anime.mal_score}</span>
      )}
      <div className="airing-card-title">{anime.title}</div>
    </div>
  );
}

// ── RecentEpisodeRow ──────────────────────────────────────────────────────────
function RecentEpisodeRow({ episode }) {
  const navigate = useNavigate();
  const url =
    `/watch/${encodeURIComponent(episode.episodeId)}` +
    `?title=${encodeURIComponent(episode.animeTitle + ' — Episodio ' + episode.number)}` +
    `&animeId=${episode.animeId}&ep=${episode.number}`;

  return (
    <div className="recent-episode-row" onClick={() => navigate(url)}>
      {episode.image ? (
        <img
          className="recent-episode-thumb"
          src={episode.image}
          alt={episode.animeTitle}
          referrerPolicy="no-referrer"
          loading="lazy"
        />
      ) : (
        <div className="recent-episode-thumb recent-episode-thumb--empty" />
      )}
      <div className="recent-episode-info">
        <p className="recent-episode-title">
          {episode.animeTitle}
          {episode.isToday && <span className="recent-episode-today">HOY</span>}
        </p>
        <p className="recent-episode-sub">Episodio {episode.number}</p>
      </div>
      <span className="recent-episode-badge">EP. {episode.number}</span>
    </div>
  );
}

// ── Home ──────────────────────────────────────────────────────────────────────
export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Hero carousel
  const [heroes,      setHeroes]      = useState([]);
  const [heroIdx,     setHeroIdx]     = useState(0);
  const [heroLoading, setHeroLoading] = useState(true);
  const heroPausedRef = useRef(false);
  const continueRef   = useRef(null);

  const scrollContinue = (dir) => {
    continueRef.current?.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  // Airing + recent episodes
  const [airingAnimes,   setAiringAnimes]   = useState([]);
  const [recentEpisodes, setRecentEpisodes] = useState([]);
  // Rotación de "Ahora en emisión": muestra 16 a la vez
  const AIRING_PAGE_SIZE = 16;
  const [airingPage,     setAiringPage]     = useState(0);
  const [airingFade,     setAiringFade]     = useState(true);  // controla CSS fade
  const airingPausedRef = useRef(false);

  // History
  const [history,       setHistory]       = useState(() => user ? [] : getHistoryLocal());
  const [historyImages, setHistoryImages] = useState({});
  const [menuOpen,      setMenuOpen]      = useState(null);

  // Load heroes
  useEffect(() => {
    setHeroLoading(true);
    const load = async () => {
      try {
        // Destacados con banner horizontal (backend); antes eran 5 búsquedas fijas
        const res = await fetch(`${API_URL}/anime/featured`);
        const loaded = res.ok ? await res.json() : [];
        setHeroes(loaded.filter((h) => h.title));
      } catch {}
      setHeroLoading(false);
    };
    load();
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (heroes.length <= 1) return;
    const timer = setInterval(() => {
      if (!heroPausedRef.current) setHeroIdx((i) => (i + 1) % heroes.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [heroes.length]);

  // Load airing + recent episodes
  useEffect(() => {
    fetch(`${API_URL}/anime/airing`)
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setAiringAnimes(data))
      .catch(() => {});

    fetch(`${API_URL}/anime/recent-episodes`)
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setRecentEpisodes(data))
      .catch(() => {});
  }, []);

  // Auto-rotar "Ahora en emisión" cada 8 s (cross-fade entre páginas)
  useEffect(() => {
    const totalPages = Math.ceil(airingAnimes.length / AIRING_PAGE_SIZE);
    if (totalPages <= 1) return;
    const timer = setInterval(() => {
      if (!airingPausedRef.current) {
        setAiringFade(false);
        setTimeout(() => {
          setAiringPage((p) => (p + 1) % totalPages);
          setAiringFade(true);
        }, 300);
      }
    }, 8000);
    return () => clearInterval(timer);
  }, [airingAnimes.length]);

  // Helpers para navegar manualmente entre páginas de airing
  const airingTotalPages = Math.ceil(airingAnimes.length / AIRING_PAGE_SIZE);
  const goAiringPage = (newPage) => {
    setAiringFade(false);
    setTimeout(() => {
      setAiringPage(newPage);
      setAiringFade(true);
    }, 200);
  };
  const airingDisplayed = airingAnimes.slice(
    airingPage * AIRING_PAGE_SIZE,
    (airingPage + 1) * AIRING_PAGE_SIZE
  );

  // Refresh history
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/user/history`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((rows) => {
          // Deduplicar por animeId: el primero = episodio más reciente (viene DESC)
          const seen = new Set();
          const deduped = rows
            .map((r) => ({
              animeId:    r.animeId,
              animeTitle: r.animeTitle,
              episodeId:  r.episodeId,
              epNum:      r.epNum,
              title:      r.title || `Episodio ${r.epNum}`,
            }))
            .filter((r) => {
              if (!r.animeId || seen.has(r.animeId)) return false;
              seen.add(r.animeId);
              return true;
            });
          setHistory(deduped);
        })
        .catch(() => {});
    } else {
      setHistory(getHistoryLocal());
    }
  }, [user]);

  // Close context menu on click outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = () => setMenuOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  const handleRemove = async (animeId) => {
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/user/history/anime/${animeId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    setHistory((prev) => prev.filter((e) => e.animeId !== animeId));
    setMenuOpen(null);
  };

  const handleMarkWatched = async (animeId) => {
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/user/history/anime/${animeId}/watched`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    setHistory((prev) => prev.filter((e) => e.animeId !== animeId));
    setMenuOpen(null);
  };

  // Fetch cover images for history
  useEffect(() => {
    if (history.length === 0) return;
    const uniqueIds = [...new Set(history.map((e) => e.animeId).filter(Boolean))];
    uniqueIds.forEach((animeId) => {
      if (historyImages[animeId]) return;
      fetch(`${API_URL}/anime/${animeId}`, { referrerPolicy: 'no-referrer' })
        .then((r) => r.json())
        .then((info) => {
          if (info.image) setHistoryImages((prev) => ({ ...prev, [animeId]: info.image }));
        })
        .catch(() => {});
    });
  }, [history]);

  return (
    <div className="page">

      {/* ── Hero carousel ── */}
      {!heroLoading && heroes.length > 0 ? (
        <section
          className="hero"
          onMouseEnter={() => { heroPausedRef.current = true; }}
          onMouseLeave={() => { heroPausedRef.current = false; }}
        >
          {heroes.map((h, i) => (
            <div
              key={h.id}
              className={`hero__slide${i === heroIdx ? ' hero__slide--active' : ''}`}
            >
              <div className="hero__bg">
                {(h.backdrop_image || h.banner_image || h.image) && (
                  <img
                    src={h.backdrop_image || h.banner_image || h.image}
                    alt=""
                    className="hero__bg-img"
                    referrerPolicy="no-referrer"
                  />
                )}
              </div>
              <div className="hero__vignette" />
              <div className="hero__content">
                <p className="hero__eyebrow">Destacado</p>
                <h1 className="hero__title">{h.title}</h1>
                <div className="hero__meta">
                  {h.releaseDate && <span>{h.releaseDate}</span>}
                  {h.status && (
                    <><span className="hero__dot">·</span><span>{statusLabel(h.status)}</span></>
                  )}
                  {h.mal_score && <RatingBadge score={h.mal_score} size="sm" />}
                </div>
                {h.genres?.length > 0 && (
                  <div className="hero__chips">
                    {h.genres.slice(0, 4).map((g) => (
                      <span key={g} className="chip">{genreLabel(g)}</span>
                    ))}
                  </div>
                )}
                {h.description && <p className="hero__desc">{h.description}</p>}
                <div className="hero__actions">
                  <Link to={`/anime/${h.id}`} className="btn-play">▶ Ver ahora</Link>
                  <Link to={`/anime/${h.id}`} className="btn-outline">Más info</Link>
                </div>
              </div>
            </div>
          ))}

          {heroes.length > 1 && (
            <div className="hero__nav">
              {heroes.map((_, i) => (
                <button
                  key={i}
                  className={`hero__nav-dot${i === heroIdx ? ' hero__nav-dot--active' : ''}`}
                  onClick={() => setHeroIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </section>
      ) : (
        <section className="hero hero--skeleton" />
      )}

      {/* ── Continuar viendo ── */}
      {history.length > 0 && (
        <section className="home-section continue-watching-section">
          <div className="home-section-header">
            <h2>Continuar viendo</h2>
          </div>
          <div className="section-scroll-wrapper">
            <button className="scroll-btn left" onClick={() => scrollContinue('left')}>‹</button>
            <div className="continue-row" ref={continueRef}>
              {history.map((entry) => (
                <div
                  key={entry.episodeId}
                  className="continue-card"
                  onClick={() => navigate(
                    `/watch/${encodeURIComponent(entry.episodeId)}` +
                    `?title=${encodeURIComponent(entry.title)}` +
                    `&animeId=${entry.animeId}&ep=${entry.epNum}`
                  )}
                >
                  {historyImages[entry.animeId] && (
                    <img
                      src={historyImages[entry.animeId]}
                      alt={entry.animeTitle}
                      className="continue-card-thumb"
                      referrerPolicy="no-referrer"
                      loading="lazy"
                    />
                  )}
                  <div className="continue-card-info">
                    <span className="continue-card-title">{entry.animeTitle}</span>
                    <span className="continue-card-ep">Episodio {entry.epNum}</span>
                    <span className="continue-card-action">▶ Continuar</span>
                  </div>
                  <div className="continue-card-menu-wrapper">
                    <button
                      className="continue-card-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(menuOpen === entry.episodeId ? null : entry.episodeId);
                      }}
                    >⋮</button>
                    {menuOpen === entry.episodeId && (
                      <div className="continue-card-dropdown">
                        <button
                          className="continue-dropdown-item"
                          onClick={(e) => { e.stopPropagation(); handleMarkWatched(entry.animeId); }}
                        >✓ Marcar como visto</button>
                        <button
                          className="continue-dropdown-item continue-dropdown-item--danger"
                          onClick={(e) => { e.stopPropagation(); handleRemove(entry.animeId); }}
                        >✕ Quitar de continuar viendo</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button className="scroll-btn right" onClick={() => scrollContinue('right')}>›</button>
          </div>
        </section>
      )}

      {/* ── Ahora en emisión + Últimos episodios ── */}
      {(airingAnimes.length > 0 || recentEpisodes.length > 0) && (
        <div className="home-dual-section">
          <div className="home-dual-left">
            {airingAnimes.length > 0 && (
              <section className="home-section">
                <div className="home-section-header">
                  <h2>Ahora en emisión</h2>
                  <span className="airing-dot">● En vivo</span>
                </div>
                <div
                  className={`airing-grid${airingFade ? '' : ' airing-grid--fade-out'}`}
                  onMouseEnter={() => { airingPausedRef.current = true; }}
                  onMouseLeave={() => { airingPausedRef.current = false; }}
                >
                  {airingDisplayed.map((anime) => (
                    <AiringCard key={anime.id} anime={anime} />
                  ))}
                </div>
                {/* Controles de paginación */}
                {airingTotalPages > 1 && (
                  <div className="airing-pagination">
                    <button
                      className="airing-page-btn"
                      onClick={() => goAiringPage((airingPage - 1 + airingTotalPages) % airingTotalPages)}
                      aria-label="Anterior"
                    >‹</button>
                    {Array.from({ length: airingTotalPages }, (_, i) => (
                      <button
                        key={i}
                        className={`airing-page-dot${i === airingPage ? ' active' : ''}`}
                        onClick={() => goAiringPage(i)}
                        aria-label={`Página ${i + 1}`}
                      />
                    ))}
                    <button
                      className="airing-page-btn"
                      onClick={() => goAiringPage((airingPage + 1) % airingTotalPages)}
                      aria-label="Siguiente"
                    >›</button>
                  </div>
                )}
              </section>
            )}
          </div>
          <div className="home-dual-right">
            {recentEpisodes.length > 0 && (
              <section className="home-section">
                <div className="home-section-header">
                  <h2>Últimos episodios</h2>
                </div>
                <div className="recent-episodes-list">
                  {recentEpisodes.map((ep) => (
                    <RecentEpisodeRow key={ep.episodeId} episode={ep} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {/* ── Filas por género ── */}
      {HOME_GENRES.map((genre) => (
        <GenreRow key={genre} genre={genre} />
      ))}

    </div>
  );
}
